import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearch } from 'wouter';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AssociationLookupSelector,
  type SelectedAssociation,
} from '@/components/business/AssociationLookupSelector';
import {
  INTAKE_ASSESSMENT_KIND_LABELS,
  INTAKE_ASSESSMENT_RECORD_KINDS,
  useCreateIntakeAssessmentRecord,
  type IntakeAssessmentRecordKind,
} from '@/hooks/useIntakeAssessment';
import {
  INTAKE_ASSESSMENT_PERMISSIONS,
  hasPermission,
} from '@/lib/intakeAssessmentPermissions';
import { IntakeAssessmentPayloadFormSlot } from './IntakeAssessmentPayloadForm';
import {
  createDefaultFormState,
  serializeFormState,
  validateFormState,
  type IntakeAssessmentPayloadFormState,
} from './intakeAssessmentPayloadFormModel';

export default function IntakeAssessmentCreatePage() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const { permissions } = useAuth();
  const { createRecord, loading, error } = useCreateIntakeAssessmentRecord();

  const initialCustomerId = (() => {
    const params = new URLSearchParams(search ?? '');
    return params.get('customerId') ?? '';
  })();
  const initialKind = (() => {
    const params = new URLSearchParams(search ?? '');
    const k = params.get('recordKind');
    if (k && INTAKE_ASSESSMENT_RECORD_KINDS.includes(k as IntakeAssessmentRecordKind)) {
      return k as IntakeAssessmentRecordKind;
    }
    return 'golden_analysis' as IntakeAssessmentRecordKind;
  })();

  const [customer, setCustomer] = useState<SelectedAssociation | null>(
    initialCustomerId
      ? { id: initialCustomerId, displayName: initialCustomerId, kind: 'customer' }
      : null,
  );
  const [recordKind, setRecordKind] =
    useState<IntakeAssessmentRecordKind>(initialKind);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [payloadState, setPayloadState] =
    useState<IntakeAssessmentPayloadFormState>(() =>
      createDefaultFormState(initialKind),
    );
  const [owner, setOwner] = useState<SelectedAssociation | null>(null);

  useEffect(() => {
    setPayloadState(createDefaultFormState(recordKind));
  }, [recordKind]);

  const fieldErrors = useMemo(
    () => validateFormState(payloadState),
    [payloadState],
  );

  const canCreate = hasPermission(
    permissions,
    INTAKE_ASSESSMENT_PERMISSIONS.CREATE,
  );
  if (!canCreate) {
    return <AccessDenied resource="接诊评估" />;
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!customer?.id) {
      toast.error('请选择客户');
      return;
    }
    if (!title.trim()) {
      toast.error('标题为必填项');
      return;
    }
    if (fieldErrors.length > 0) {
      toast.error(fieldErrors[0].message);
      return;
    }
    const payload = serializeFormState(payloadState);
    const result = await createRecord({
      customerId: customer.id,
      recordKind,
      title: title.trim(),
      summary: summary.trim() || undefined,
      payload,
      ownerUserId: owner?.id ?? undefined,
    });
    if (result) {
      toast.success('记录已创建为草稿');
      navigate(`/intake-assessment/records/${result.id}`);
    } else if (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <button
            type="button"
            className="text-sm text-muted-foreground hover:underline"
            onClick={() => navigate('/intake-assessment/records')}
          >
            ← 返回列表
          </button>
          <h1 className="text-xl font-semibold">新建接诊评估记录</h1>
          <p className="text-sm text-muted-foreground">
            创建草稿后再提交审核；只有审核通过的记录才会被方案模块引用。
          </p>
        </div>
      </div>

      <Card className="p-4">
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="ia-kind">记录类型</Label>
              <select
                id="ia-kind"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={recordKind}
                onChange={(e) =>
                  setRecordKind(e.target.value as IntakeAssessmentRecordKind)
                }
              >
                {INTAKE_ASSESSMENT_RECORD_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {INTAKE_ASSESSMENT_KIND_LABELS[k]}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ia-customer">客户 *</Label>
              <AssociationLookupSelector
                id="ia-customer"
                kind="customer"
                value={customer}
                onChange={setCustomer}
                placeholder="按姓名搜索客户…"
                required
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="ia-title">标题 *</Label>
              <Input
                id="ia-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="例如：张三 首次接诊评估"
                maxLength={255}
                required
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="ia-summary">摘要（列表展示用，避免 PHI 明文）</Label>
              <textarea
                id="ia-summary"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                rows={2}
                maxLength={1000}
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="一句话总结，例如：客户预算 12 万元，关注疗效与服务节奏。"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ia-owner">负责人（可选，默认当前用户）</Label>
              {/*
                ownerUserId 在 intake_assessment_records 表里是 users.id
                （IntakeAssessmentService 默认回填 ctx.userId）。这里必须
                用 kind="user" 检索系统账号，绝不能用 kind="employee"，
                否则会把 hr_employees.id 写入 ownerUserId 字段并破坏
                责任人/重分派校验。Issue #344 P1 修复点。
              */}
              <AssociationLookupSelector
                id="ia-owner"
                kind="user"
                value={owner}
                onChange={setOwner}
                placeholder="搜索负责人姓名…"
              />
            </div>
          </div>

          <div className="space-y-2 border-t pt-4">
            <h2 className="text-sm font-medium">
              详情 · {INTAKE_ASSESSMENT_KIND_LABELS[recordKind]}
            </h2>
            <IntakeAssessmentPayloadFormSlot
              state={payloadState}
              onChange={setPayloadState}
              errors={fieldErrors}
            />
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error.message}
            </div>
          )}
          <div className="flex gap-2">
            <Button type="submit" disabled={loading}>
              {loading ? '创建中…' : '创建草稿'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/intake-assessment/records')}
            >
              取消
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
